import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";
import { supabase } from "@/utilities/supabase";

/**
 * REP-199 Phase 1 — Activity slice for the parent-deal Activity tab.
 *
 * Lives alongside `slices/activities/activitiesApi.ts` (team/my feed)
 * but exposes a single, deal-scoped endpoint with cursor pagination.
 * Mounts on the existing `/api/activities` resource (singular path was
 * requested in the brief, but mounting on the plural keeps it consistent
 * with the team / my endpoints — only `/` differs from those).
 */

export type ActivityType =
  // Documents
  | "DOCUMENT_CREATED"
  | "DOCUMENT_PUBLISHED"
  | "DOCUMENT_READY_FOR_SIGNING"
  | "DOCUMENT_SIGNED"
  | "DOCUMENT_COMPLETED"
  // Deals
  | "DEAL_CREATED"
  | "DEAL_CLOSED"
  // Events
  | "EVENT_CREATED"
  | "EVENT_UPDATED"
  | "EVENT_COMPLETED"
  // Contacts
  | "CONTACT_CREATED"
  | "CONTACT_UPDATED"
  | "CONTACT_REMOVED"
  // Properties
  | "PROPERTY_CREATED"
  | "PROPERTY_UPDATED"
  | "PROPERTY_DATA_UPDATED"
  // Steps
  | "STEP_COMPLETED"
  // Client Needs
  | "CLIENT_NEED_CREATED"
  | "CLIENT_NEED_FILLED"
  | "CLIENT_NEED_MATCHED"
  | "CLIENT_NEED_CONVERTED"
  // Listing Sheets
  | "LISTING_SHEET_PUBLISHED"
  // Property-related
  | "PROPERTY_NOTE_ADDED"
  | "PROPERTY_DOCUMENT_ADDED"
  | "PROPERTY_PROMOTED_PRIMARY"
  | "PROPERTY_PASSED"
  // Notes
  | "NOTE_ADDED"
  // Templates
  | "DOCUMENT_TEMPLATE_CREATED"
  | "DOCUMENT_TEMPLATE_UPDATED"
  | "WORKFLOW_TEMPLATE_CREATED"
  | "WORKFLOW_TEMPLATE_UPDATED"
  // OCR
  | "OCR_BATCH_CREATED"
  | "OCR_BATCH_DOCUMENT_ADDED"
  | "OCR_BATCH_DOCUMENT_REVIEWED"
  // Project graph
  | "SUB_PROJECT_CREATED"
  | "MERGED_PROJECT_LINKED"
  | "SELLER_REP_LINKED"
  | "CONTRACT_FAILED_DROP_BACK"
  // Teams
  | "TEAM_MERGED"
  | "TEAM_UPDATED"
  | "TEAM_MEMBER_ADDED"
  | "TEAM_MEMBER_REMOVED"
  // Document linkage / assignment
  | "DOCUMENT_ASSIGNED"
  | "DOCUMENT_PARTICIPANT_ADDED"
  | "STEP_DOCUMENT_ADDED"
  | "STEP_DOCUMENT_REMOVED"
  // Smart fields
  | "FIELD_SYNCED";

export interface Activity {
  id: string;
  type: ActivityType;
  actorId: string;
  actorName: string;
  companyId: string;
  entityType: string;
  entityId: string;
  entityName: string | null;
  parentType: string | null;
  parentId: string | null;
  parentName: string | null;
  metadata: Record<string, any> | null;
  createdAt: string;
  /**
   * v13 scope context populated by the backend `getDealActivity` endpoint:
   *  - 'deal'    → row is deal-wide
   *  - 'pursuit' → row is on a specific pursuit
   *  - null      → not derivable
   * `scopeLabel` is the human label ("Deal-wide" or the pursuit name)
   * rendered inline as a small pill at the end of the activity row.
   */
  scope?: "deal" | "pursuit" | null;
  scopeLabel?: string | null;
}

export interface DealActivityResponse {
  data: Activity[];
  nextCursor: string | null;
  total: number;
}

export interface GetDealActivityParams {
  dealId: string;
  limit?: number;
  nextId?: string;
}

/**
 * Cross-system person activity feed (Contacts drawer).
 *
 * Hits `GET /api/persons/:id/activity` — note this lives under the
 * `persons` resource, NOT `activities`, so it needs its own base query
 * rather than the slice-wide `/api/activities` base. The backend agent
 * built the route; until it ships, callers MUST treat 404 / undefined
 * gracefully (the drawer falls back to its locally-derived timeline).
 */
export interface PersonActivityResponse {
  data: Activity[];
  nextCursor?: string | null;
  total?: number;
}

export interface GetPersonActivityParams {
  personId: string;
  limit?: number;
  nextId?: string;
}

// A persons-scoped base query (auth header + `/api/persons` base) used by
// the person-activity endpoint only. Mirrors createBaseApiWithAuth's
// prepareHeaders so the JWT is attached the same way.
const personsBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = fetchBaseQuery({
  baseUrl: "/api/persons",
  prepareHeaders: async (headers) => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session?.access_token) {
      headers.set("authorization", `Bearer ${session.access_token}`);
    }
    return headers;
  },
});

export const activityApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "activityApi",
    baseUrl: "/api/activities",
  }),
  reducerPath: "activityApi",
  tagTypes: ["DealActivity"],
  endpoints: (builder) => ({
    getDealActivity: builder.query<DealActivityResponse, GetDealActivityParams>({
      query: (params) => {
        const queryParams: Record<string, string> = {
          dealId: params.dealId,
        };
        if (params.limit !== undefined) queryParams.limit = params.limit.toString();
        if (params.nextId) queryParams.nextId = params.nextId;
        return {
          url: "/",
          params: queryParams,
        };
      },
      // Merge incremental pages into a single list for infinite scroll.
      // We dedupe by id (cursor pagination guarantees no overlap, but
      // RTK Query may re-fire the initial page when the cache key is
      // first hydrated).
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}:${queryArgs.dealId}`;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (!arg.nextId) {
          // Fresh load — replace the cache
          return newItems;
        }
        // Pagination — append new rows; dedupe by id
        const seen = new Set(currentCache.data.map((a) => a.id));
        const additions = newItems.data.filter((a) => !seen.has(a.id));
        return {
          data: [...currentCache.data, ...additions],
          nextCursor: newItems.nextCursor,
          total: newItems.total,
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        // Refetch when the dealId changes OR when nextId changes (next page)
        return (
          currentArg?.dealId !== previousArg?.dealId ||
          currentArg?.nextId !== previousArg?.nextId
        );
      },
      providesTags: (result, _err, arg) =>
        result
          ? [
              { type: "DealActivity" as const, id: arg.dealId },
              ...result.data.map((a) => ({
                type: "DealActivity" as const,
                id: a.id,
              })),
            ]
          : [{ type: "DealActivity" as const, id: arg.dealId }],
    }),

    /**
     * GET /api/persons/:id/activity — combined cross-system feed for the
     * Contacts drawer (notes + events + deals + listings + needs …).
     * Uses a dedicated persons-scoped base query (see `personsBaseQuery`).
     * Returns `{ data, nextCursor, total }`; the drawer handles 404/empty.
     */
    getPersonActivity: builder.query<PersonActivityResponse, GetPersonActivityParams>({
      queryFn: async (arg, api, extraOptions) => {
        const params: Record<string, string> = {};
        if (arg.limit !== undefined) params.limit = arg.limit.toString();
        if (arg.nextId) params.nextId = arg.nextId;
        const result = await personsBaseQuery(
          { url: `/${arg.personId}/activity`, params },
          api,
          extraOptions,
        );
        if (result.error) {
          return { error: result.error };
        }
        return { data: result.data as PersonActivityResponse };
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}:${queryArgs.personId}`,
      merge: (currentCache, newItems, { arg }) => {
        if (!arg.nextId) return newItems;
        const seen = new Set(currentCache.data.map((a) => a.id));
        const additions = newItems.data.filter((a) => !seen.has(a.id));
        return {
          data: [...currentCache.data, ...additions],
          nextCursor: newItems.nextCursor,
          total: newItems.total,
        };
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.personId !== previousArg?.personId ||
        currentArg?.nextId !== previousArg?.nextId,
    }),
  }),
});

export const { useGetDealActivityQuery, useGetPersonActivityQuery } = activityApi;
